import type { PaymentProcessingChangeHandler } from '@elfsight/lib-stripe-integration';
import { PaymentSessionMetadataProvider } from '@elfsight/lib-stripe-integration';
import type { CheckoutColors, CheckoutPaymentProps } from '../common';
import { STYLE_DEFAULTS } from '../common';
import { ensureCheckoutSettingsPrepared } from './components';
import type { CheckoutSummaryProps } from './components/layouts/summary';
import type {
  CheckoutDictionary,
  PriceComponent
} from './components/providers';
import {
  CheckoutColorsProvider,
  CheckoutFormSubmitProvider,
  CheckoutI18nProvider,
  CheckoutItemProvider,
  CheckoutNavigationProvider
} from './components/providers';
import { CheckoutComponentLayout } from './layouts/checkout-component-layout';

export type CheckoutComponentLayoutProps = CheckoutPaymentProps & {
  summarySettings: CheckoutSummaryProps;
};

export type CheckoutComponentProps = CheckoutComponentLayoutProps &
  Partial<CheckoutColors> & {
    isOpen: boolean;
    handleCloseRequest: () => void;
    dictionary: CheckoutDictionary;
    fullscreen?: boolean;
    onPaymentProcessing?: PaymentProcessingChangeHandler;
    PriceComponent?: PriceComponent;
  };

export function CheckoutComponent({
  isOpen,
  handleCloseRequest,
  onPaymentProcessing,

  textColor = STYLE_DEFAULTS.textColor,
  accentColor = STYLE_DEFAULTS.accentColor,
  errorColor = STYLE_DEFAULTS.errorColor,
  backgroundColor = STYLE_DEFAULTS.backgroundColor,
  successColor = STYLE_DEFAULTS.successColor,

  summarySettings,
  dictionary,
  fullscreen = true,
  PriceComponent,

  ...forwardingProps
}: CheckoutComponentProps) {
  const { withInternalForm } = forwardingProps;
  const preparedSettings = ensureCheckoutSettingsPrepared(forwardingProps);

  return (
    <CheckoutI18nProvider
      dictionary={dictionary}
      PriceComponent={PriceComponent}
    >
      <CheckoutNavigationProvider
        isCheckoutOpen={isOpen}
        closeCheckoutComponent={handleCloseRequest}
        withForm={withInternalForm}
      >
        <CheckoutItemProvider summarySettings={summarySettings}>
          <PaymentSessionMetadataProvider
            onPaymentProcessing={onPaymentProcessing}
          >
            <CheckoutColorsProvider
              textColor={textColor}
              accentColor={accentColor}
              errorColor={errorColor}
              successColor={successColor}
              backgroundColor={backgroundColor}
            >
              <CheckoutFormSubmitProvider>
                <CheckoutComponentLayout
                  paymentComponentProps={preparedSettings}
                  summaryComponentProps={summarySettings}
                  fullscreen={fullscreen}
                />
              </CheckoutFormSubmitProvider>
            </CheckoutColorsProvider>
          </PaymentSessionMetadataProvider>
        </CheckoutItemProvider>
      </CheckoutNavigationProvider>
    </CheckoutI18nProvider>
  );
}
